import React from "react"
import { connect } from "react-redux"
import {
  compose,
  withStateHandlers,
  defaultProps,
  withProps,
  mapProps,
} from "recompact"
import { PrimaryButton } from "@infotech/uikit-button"
import { BackIcon } from "@infotech/uikit-icons"
import * as entity from "src/engine/modules/entity"
import withDynamicHOC from "src/hocs/withDynamicHOC"
import { Flex, ContentBox } from "src/components/Grid"
import { Box } from "@infotech/uikit-grid"
import ModalConfirm from "src/components/modals/ModalConfirm"
import allConstraintTypes from "src/hocs/allConstraintTypes"
import withConstraints from "src/hocs/constraints"
import constraintModel from "src/models/constraint"
import {
  findConstraintTypeForConstraint,
  filterConstraintsByType,
  categories,
  constraintAPI,
} from "./helpers"
import ConstraintList from "./ConstraintList"
import ConstraintTypeList from "./ConstraintTypeList"
import ConstraintForm from "./ConstraintForm"
import ConstraintLayout from "./ConstraintLayout"
import styles from "./styles.css"

const ViewTab = props => {
  const { children } = props

  return <Box style={{ height: `100%` }}>{children}</Box>
}

const EditTab = props => {
  const { header, leftContent, rightContent } = props
  return (
    <Box loose flex column>
      <Box loose className={styles.EditTabHeader}>
        <Box loose flex pl="2md" pt="2md">
          {header}
        </Box>
      </Box>
      <Flex style={{ height: `100%` }}>
        <Box br className={styles.EditTabColumnWrapper}>
          {leftContent}
        </Box>
        <Box br className={styles.EditTabColumnWrapper}>
          {rightContent}
        </Box>
      </Flex>
    </Box>
  )
}

const EmptyList = ({ header, text, button }) => (
  <Box loose pt="2md">
    <Flex className={styles.emptyConstraintList} column align="center">
      <h2 style={{ margine: 0, padding: 0 }}>{header}</h2>
      <span className={styles.emptyConstraintListText}>{text}</span>
      <ContentBox>
        <Flex justify="space-between" w="100%">
          {button}
        </Flex>
      </ContentBox>
    </Flex>
  </Box>
)

const withConstraintDeleting = ({ to = `deletingModalAPI` }) =>
  compose(
    mapProps(props => ({ outerProps: props })),

    withDynamicHOC(
      () => false,
      ({ outerProps: { planId, basicUrl } }) =>
        connect(null, {
          deleteConstraint: constraint =>
            entity.actions.creators.remove(
              constraintModel(planId, basicUrl),
              constraintAPI.getId(constraint),
            ),
        }),
    ),

    withStateHandlers(
      { deletingConstraint: null },
      {
        openDeletingModal: () => constraint => ({
          deletingConstraint: constraint,
        }),
        closeDeletingModal: () => () => ({ deletingConstraint: null }),
      },
    ),

    BaseComponent => props => {
      const {
        outerProps,
        openDeletingModal,
        closeDeletingModal,
        deletingConstraint,
        deleteConstraint,
      } = props

      const api = {
        [to]: {
          openDeletingModal,
          closeDeletingModal,
        },
      }

      return (
        <>
          <BaseComponent {...outerProps} {...api} />
          <ModalConfirm
            isOpen={Boolean(deletingConstraint)}
            title="Вы действительно хотите удалить ограничение?"
            buttonConf={[
              {
                text: `Удалить`,
                onClick: () => {
                  deleteConstraint(deletingConstraint)
                  closeDeletingModal()
                },
                isOk: true,
                done: true,
                disabled: false,
              },
              {
                text: `Отмена`,
                onClick: closeDeletingModal,
                disabled: false,
              },
            ]}
            isRemove
          />
        </>
      )
    },
  )

const withCollections = compose(
  mapProps(props => ({
    outerProps: props,
    planId: props.planId,
    basicUrl: props.basicUrl,
  })),

  // It uses planId prop
  withConstraints({ to: `constraintCollection` }),

  allConstraintTypes({ to: `constraintTypeCollection` }),

  mapProps(
    ({ outerProps, constraintCollection, constraintTypeCollection }) => ({
      constraintCollection,
      constraintTypeCollection,
      ...outerProps,
    }),
  ),
)

const VIEW_TAB = `Список ограничений плана`
const EDIT_TAB = `Создание ограничения плана`
const NEW_CONSTRAINT = `NEW_CONSTRAINT`

export default compose(
  defaultProps({ categories }),

  // It uses planId prop
  withCollections,

  withProps(({ constraintTypeCollection, constraintCollection }) => ({
    constraintTypes: constraintTypeCollection.data,
    constraintTypesPending: constraintTypeCollection.pending,
    constraints: constraintCollection.data,
    constraintsPending: constraintCollection.pending,
  })),

  // It uses planId prop
  withConstraintDeleting({ to: `deletingModalAPI` }),

  withStateHandlers(
    {
      currentTab: VIEW_TAB,
      selectedConstraintType: null,
      selectedConstraint: null,
      expandedCategories: [...categories],
    },
    {
      goToView: () => () => ({ currentTab: VIEW_TAB }),
      goToEdit: () => () => ({ currentTab: EDIT_TAB }),
      toggleConstraintType: ({
        selectedConstraintType,
        selectedConstraint,
      }) => constraintType => ({
        selectedConstraintType:
          selectedConstraint || selectedConstraintType !== constraintType
            ? constraintType
            : null,
        selectedConstraint: null,
      }),
      toggleConstraint: (
        { selectedConstraint },
        { constraintTypes },
      ) => constraint => ({
        selectedConstraint:
          selectedConstraint !== constraint ? constraint : null,
        currentTab: EDIT_TAB,
        ...(constraint && constraint !== NEW_CONSTRAINT
          ? {
              selectedConstraintType: findConstraintTypeForConstraint(
                constraintTypes,
                constraint,
              ),
            }
          : {}),
      }),
      toggleCategory: ({ expandedCategories }) => category => ({
        expandedCategories: expandedCategories.some(
          expandedCategory => expandedCategory === category,
        )
          ? expandedCategories.filter(
              expandedCategory => expandedCategory !== category,
            )
          : [...expandedCategories, category],
      }),
    },
  ),
)(props => {
  const {
    basicUrl,
    currentTab,
    goToView,
    goToEdit,
    toggleConstraintType,
    toggleConstraint,
    selectedConstraint,
    selectedConstraintType,
    constraintTypes,
    constraints,
    categories,
    expandedCategories,
    toggleCategory,
    planId,
    deletingModalAPI: { openDeletingModal },
    constraintTypesPending,
    constraintsPending,
    isDisabled,
  } = props

  const pending = constraintTypesPending || constraintsPending

  const headerLabel =
    currentTab === EDIT_TAB ? (
      <span className={styles.headline} onClick={goToView}>
        <BackIcon color="#adadad" />
        <span>Список ограничений</span>
      </span>
    ) : (
      currentTab
    )

  const getBody = () => {
    if (currentTab === EDIT_TAB) {
      const getRightContent = () => {
        if (selectedConstraint) {
          return (
            <ConstraintForm
              planId={planId}
              basicUrl={basicUrl}
              constraint={
                selectedConstraint === NEW_CONSTRAINT
                  ? null
                  : selectedConstraint
              }
              constraintType={selectedConstraintType}
              onCancel={() => toggleConstraint(selectedConstraint)}
              isDisabled={isDisabled}
            />
          )
        }

        if (selectedConstraintType) {
          const createButton = (
            <PrimaryButton
              onClick={() => toggleConstraint(NEW_CONSTRAINT)}
              type="button"
              disabled={isDisabled}
            >
              ДОБАВИТЬ ПРАВИЛО
            </PrimaryButton>
          )

          if (
            filterConstraintsByType(constraints, selectedConstraintType).length
          ) {
            return (
              <ConstraintLayout
                bodyContent={
                  <ConstraintList
                    constraints={filterConstraintsByType(
                      constraints,
                      selectedConstraintType,
                    )}
                    constraintTypes={constraintTypes}
                    onEditClick={toggleConstraint}
                    onDeleteClick={openDeletingModal}
                    isDisabled={isDisabled}
                  />
                }
                buttons={createButton}
              />
            )
          }

          return (
            <EmptyList
              header="ПРАВИЛА ОГРАНИЧЕНИЯ"
              text={
                <>
                  Для добавления правила нажмите
                  <br />
                  <Box loose pl="2md">
                    на соответсвующую кнопку
                  </Box>
                </>
              }
              button={createButton}
            />
          )
        }

        return null
      }

      return (
        <EditTab
          header={headerLabel}
          rightContent={getRightContent()}
          leftContent={
            <ConstraintTypeList
              constraintTypes={constraintTypes}
              onOptionClick={toggleConstraintType}
              categories={categories}
              selectedConstraintType={selectedConstraintType}
              onGroupClick={toggleCategory}
              expandedCategories={expandedCategories}
            />
          }
        />
      )
    }

    if (currentTab === VIEW_TAB) {
      const getChildren = () => {
        const addConstraintButton = (
          <PrimaryButton onClick={goToEdit} disabled={isDisabled}>
            ДОБАВИТЬ ОГРАНИЧЕНИЕ
          </PrimaryButton>
        )

        if (constraints.length) {
          return (
            <Box loose className={styles.ConstraintListWrapper}>
              <ContentBox bb>{addConstraintButton}</ContentBox>
              <ConstraintList
                constraints={constraints}
                constraintTypes={constraintTypes}
                onEditClick={toggleConstraint}
                onDeleteClick={openDeletingModal}
                useGroups
                categories={categories}
                onGroupClick={toggleCategory}
                expandedCategories={expandedCategories}
                displayViolations
                isDisabled={isDisabled}
              />
            </Box>
          )
        }

        if (!pending) {
          return (
            <Box
              style={{
                height: `100%`,
              }}
              flex
              column
              pt="3md"
              align="center"
            >
              <EmptyList
                header="Начало работы с ограничениями"
                text={
                  <>
                    Ограничения — это правила и условия, которым должен
                    <br />
                    соответствовать план, а также его ресурсы и события.
                  </>
                }
                button={addConstraintButton}
              />
            </Box>
          )
        }

        return null
      }

      return <ViewTab>{getChildren()}</ViewTab>
    }
    return null
  }

  return <Box style={{ height: `100%` }}>{getBody()}</Box>
})
